/**
 * Book Appointment — Web Component
 * Multi-step appointment booking form with calendar, time slots,
 * and Google Sheets integration.
 */

const WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const PREFIX = 'section-book-appointment';

class BookAppointment extends HTMLElement {
  connectedCallback() {
    this.currentStep = 1;
    this.formData = {
      preference: '',
      city: '',
      pincode: '',
      storeLocation: '',
      purposes: [],
      date: null,
      time: '',
      contactNumber: '',
      firstName: '',
      lastName: '',
      email: '',
    };

    const now = new Date();
    this.calendarYear = now.getFullYear();
    this.calendarMonth = now.getMonth();
    this.today = now;

    this.googleScriptUrl = this.dataset.googleScriptUrl ?? '';
    this.sendEmail = this.dataset.sendEmail === 'true';

    this.cacheElements();
    this.renderCalendar();
    this.bindEvents();
  }

  cacheElements() {
    this.stepperSteps = [...this.querySelectorAll('[data-stepper-step]')];
    this.stepperLines = [...this.querySelectorAll('[data-stepper-line]')];
    this.stepContents = [...this.querySelectorAll('[data-step-content]')];
    this.formArea = this.querySelector('[data-form-area]');
    this.confirmation = this.querySelector('[data-confirmation]');
    this.calendarDays = this.querySelector('[data-calendar-days]');
    this.calendarTitle = this.querySelector('[data-calendar-title]');
    this.timeslotGrid = this.querySelector('[data-timeslot-grid]');
    this.summaryBody = this.querySelector('[data-summary-body]');
    this.toast = this.querySelector('[data-toast]');
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const { target } = event;

      const nextBtn = target.closest('[data-action="next"]');
      if (nextBtn) {
        this.handleNext();
        return;
      }

      const cancelBtn = target.closest('[data-action="cancel"]');
      if (cancelBtn) {
        this.handleCancel();
        return;
      }

      const submitBtn = target.closest('[data-action="submit"]');
      if (submitBtn) {
        this.handleSubmit();
        return;
      }

      const purposeOption = target.closest('[data-purpose-option]');
      if (purposeOption) {
        this.togglePurpose(purposeOption);
        return;
      }

      const calendarDay = target.closest('[data-calendar-day]');
      if (calendarDay && !calendarDay.classList.contains(`${PREFIX}__calendar-day--disabled`) && !calendarDay.classList.contains(`${PREFIX}__calendar-day--empty`)) {
        this.selectDate(calendarDay);
        return;
      }

      const timeslot = target.closest('[data-timeslot]');
      if (timeslot && !timeslot.classList.contains(`${PREFIX}__timeslot--disabled`)) {
        this.selectTimeslot(timeslot);
        return;
      }

      const calendarPrev = target.closest('[data-calendar-prev]');
      if (calendarPrev) {
        this.navigateMonth(-1);
        return;
      }

      const calendarNext = target.closest('[data-calendar-next]');
      if (calendarNext) {
        this.navigateMonth(1);
        return;
      }
    });

    this.addEventListener('keydown', (event) => {
      const purposeOption = event.target.closest('[data-purpose-option]');
      if (purposeOption && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.togglePurpose(purposeOption);
      }

      const timeslot = event.target.closest('[data-timeslot]');
      if (timeslot && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.selectTimeslot(timeslot);
      }

      const calendarDay = event.target.closest('[data-calendar-day]');
      if (calendarDay && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        if (!calendarDay.classList.contains(`${PREFIX}__calendar-day--disabled`) && !calendarDay.classList.contains(`${PREFIX}__calendar-day--empty`)) {
          this.selectDate(calendarDay);
        }
      }
    });
  }

  /* =========================================
     Step Navigation
     ========================================= */

  showStep(step) {
    this.currentStep = step;

    for (const content of this.stepContents) {
      const contentStep = Number(content.dataset.stepContent);
      content.classList.toggle(`${PREFIX}__step-content--active`, contentStep === step);
    }

    this.updateStepper(step);

    const activeContent = this.querySelector(`[data-step-content="${step}"]`);
    if (activeContent) {
      const firstInput = activeContent.querySelector('input, select, button');
      firstInput?.focus({ preventScroll: true });
    }

    this.scrollToTop();
  }

  updateStepper(step) {
    for (const stepEl of this.stepperSteps) {
      const stepNum = Number(stepEl.dataset.stepperStep);
      stepEl.classList.remove(`${PREFIX}__step--active`, `${PREFIX}__step--completed`);

      if (stepNum === step) {
        stepEl.classList.add(`${PREFIX}__step--active`);
      } else if (stepNum < step) {
        stepEl.classList.add(`${PREFIX}__step--completed`);
      }
    }

    for (const line of this.stepperLines) {
      const lineAfterStep = Number(line.dataset.stepperLine);
      line.classList.toggle(`${PREFIX}__step-line--active`, lineAfterStep < step);
    }
  }

  handleNext() {
    if (!this.validateStep(this.currentStep)) return;
    this.collectStepData(this.currentStep);

    if (this.currentStep < 3) {
      this.showStep(this.currentStep + 1);
    }
  }

  handleCancel() {
    if (this.currentStep > 1) {
      this.showStep(this.currentStep - 1);
    }
  }

  scrollToTop() {
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* =========================================
     Validation
     ========================================= */

  validateStep(step) {
    this.clearErrors();

    if (step === 1) {
      return this.validateStep1();
    }

    if (step === 2) {
      return this.validateStep2();
    }

    if (step === 3) {
      return this.validateStep3();
    }

    return true;
  }

  validateStep1() {
    let valid = true;
    const preference = this.querySelector('[data-field="preference"]');

    if (preference && !preference.value) {
      this.showFieldError(preference, 'Please select a preference');
      valid = false;
    }

    const city = this.querySelector('[data-field="city"]');
    if (city) {
      const value = city.value.trim();
      if (!value) {
        this.showFieldError(city, 'Please enter your city or country');
        valid = false;
      } else if (!/^[A-Za-z][A-Za-z\s.,'\/-]*$/.test(value)) {
        this.showFieldError(city, 'Please enter a valid city or country (letters only)');
        valid = false;
      }
    }

    const pincode = this.querySelector('[data-field="pincode"]');
    if (pincode) {
      const value = pincode.value.trim();
      if (!value) {
        this.showFieldError(pincode, 'Please enter your pincode');
        valid = false;
      } else if (!/^\d{6}$/.test(value)) {
        this.showFieldError(pincode, 'Please enter a valid 6-digit pincode');
        valid = false;
      }
    }

    const storeLocation = this.querySelector('[data-field="storeLocation"]');
    if (storeLocation && !storeLocation.value.trim()) {
      this.showFieldError(storeLocation, 'Please enter a store location');
      valid = false;
    }

    const selectedPurposes = this.querySelectorAll(`[data-purpose-option].${PREFIX}__purpose-option--selected`);
    if (selectedPurposes.length === 0) {
      this.showToast('Please select at least one purpose', true);
      valid = false;
    }

    return valid;
  }

  validateStep2() {
    if (!this.formData.date) {
      this.showToast('Please select a date', true);
      return false;
    }

    if (!this.formData.time) {
      this.showToast('Please select a time slot', true);
      return false;
    }

    return true;
  }

  validateStep3() {
    let valid = true;
    const fields = [
      { name: 'contactNumber', message: 'Please enter your contact number' },
      { name: 'firstName', message: 'Please enter your first name' },
      { name: 'lastName', message: 'Please enter your last name' },
      { name: 'email', message: 'Please enter your email' },
    ];

    for (const field of fields) {
      const input = this.querySelector(`[data-field="${field.name}"]`);
      if (input && !input.value.trim()) {
        this.showFieldError(input, field.message);
        valid = false;
      }
    }

    const nameFields = [
      { name: 'firstName', message: 'Please enter a valid first name (letters only)' },
      { name: 'lastName', message: 'Please enter a valid last name (letters only)' },
    ];
    for (const field of nameFields) {
      const input = this.querySelector(`[data-field="${field.name}"]`);
      const value = input?.value.trim();
      if (value && !/^[A-Za-z][A-Za-z\s'.-]*$/.test(value)) {
        this.showFieldError(input, field.message);
        valid = false;
      }
    }

    const phoneInput = this.querySelector('[data-field="contactNumber"]');
    const phoneValue = phoneInput?.value.trim();
    if (phoneValue) {
      const sanitized = phoneValue.replace(/[\s()-]/g, '');
      if (!/^\+?\d{10,15}$/.test(sanitized)) {
        this.showFieldError(phoneInput, 'Please enter a valid contact number');
        valid = false;
      }
    }

    const emailInput = this.querySelector('[data-field="email"]');
    if (emailInput?.value.trim() && !this.isValidEmail(emailInput.value.trim())) {
      this.showFieldError(emailInput, 'Please enter a valid email address');
      valid = false;
    }

    return valid;
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  showFieldError(input, message) {
    input.classList.add(`${PREFIX}__input--error`);
    const errorEl = input.parentElement.querySelector(`[data-error]`);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.classList.add(`${PREFIX}__error-text--visible`);
    }
  }

  clearErrors() {
    const errorInputs = this.querySelectorAll(`.${PREFIX}__input--error, .${PREFIX}__select--error`);
    for (const el of errorInputs) {
      el.classList.remove(`${PREFIX}__input--error`, `${PREFIX}__select--error`);
    }

    const errorTexts = this.querySelectorAll(`.${PREFIX}__error-text--visible`);
    for (const el of errorTexts) {
      el.classList.remove(`${PREFIX}__error-text--visible`);
    }
  }

  /* =========================================
     Data Collection
     ========================================= */

  collectStepData(step) {
    if (step === 1) {
      const preference = this.querySelector('[data-field="preference"]');
      const city = this.querySelector('[data-field="city"]');
      const pincode = this.querySelector('[data-field="pincode"]');
      const storeLocation = this.querySelector('[data-field="storeLocation"]');

      this.formData.preference = preference?.value ?? '';
      this.formData.city = city?.value ?? '';
      this.formData.pincode = pincode?.value ?? '';
      this.formData.storeLocation = storeLocation?.value ?? '';

      const selectedPurposes = this.querySelectorAll(`[data-purpose-option].${PREFIX}__purpose-option--selected`);
      this.formData.purposes = [...selectedPurposes].map((el) => el.dataset.purposeLabel);
    }

    if (step === 3) {
      const contactNumber = this.querySelector('[data-field="contactNumber"]');
      const firstName = this.querySelector('[data-field="firstName"]');
      const lastName = this.querySelector('[data-field="lastName"]');
      const email = this.querySelector('[data-field="email"]');

      this.formData.contactNumber = contactNumber?.value ?? '';
      this.formData.firstName = firstName?.value ?? '';
      this.formData.lastName = lastName?.value ?? '';
      this.formData.email = email?.value ?? '';
    }
  }

  /* =========================================
     Purpose Selection
     ========================================= */

  togglePurpose(option) {
    option.classList.toggle(`${PREFIX}__purpose-option--selected`);
    const isSelected = option.classList.contains(`${PREFIX}__purpose-option--selected`);
    option.setAttribute('aria-pressed', String(isSelected));

    const radio = option.querySelector('input[type="checkbox"]');
    if (radio) {
      radio.checked = isSelected;
    }
  }

  /* =========================================
     Calendar
     ========================================= */

  renderCalendar() {
    if (!this.calendarDays || !this.calendarTitle) return;

    const year = this.calendarYear;
    const month = this.calendarMonth;

    this.calendarTitle.textContent = `${MONTHS[month]} ${year}`;

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    let startDayIndex = firstDay.getDay() - 1;
    if (startDayIndex < 0) startDayIndex = 6;

    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const todayStr = this.formatDateISO(this.today);
    const selectedStr = this.formData.date ?? '';

    let html = '';

    for (let i = startDayIndex - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      html += `<button type="button" class="${PREFIX}__calendar-day ${PREFIX}__calendar-day--other-month ${PREFIX}__calendar-day--disabled" tabindex="-1" aria-disabled="true">${day}</button>`;
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(year, month, day);
      const dateStr = this.formatDateISO(dateObj);
      const isPast = dateObj < new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate());
      const isToday = dateStr === todayStr;
      const isSelected = dateStr === selectedStr;

      let classes = `${PREFIX}__calendar-day`;
      if (isPast) classes += ` ${PREFIX}__calendar-day--disabled`;
      if (isToday) classes += ` ${PREFIX}__calendar-day--today`;
      if (isSelected) classes += ` ${PREFIX}__calendar-day--selected`;

      const disabled = isPast ? 'aria-disabled="true" tabindex="-1"' : 'tabindex="0"';

      html += `<button type="button" class="${classes}" data-calendar-day data-date="${dateStr}" ${disabled}>${day}</button>`;
    }

    const totalCells = startDayIndex + daysInMonth;
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let i = 1; i <= remainingCells; i++) {
      html += `<button type="button" class="${PREFIX}__calendar-day ${PREFIX}__calendar-day--other-month ${PREFIX}__calendar-day--disabled" tabindex="-1" aria-disabled="true">${i}</button>`;
    }

    this.calendarDays.innerHTML = html;

    const prevBtn = this.querySelector('[data-calendar-prev]');
    if (prevBtn) {
      const isPrevDisabled = year === this.today.getFullYear() && month <= this.today.getMonth();
      prevBtn.disabled = isPrevDisabled;
    }
  }

  navigateMonth(direction) {
    this.calendarMonth += direction;

    if (this.calendarMonth > 11) {
      this.calendarMonth = 0;
      this.calendarYear += 1;
    } else if (this.calendarMonth < 0) {
      this.calendarMonth = 11;
      this.calendarYear -= 1;
    }

    this.renderCalendar();
  }

  selectDate(dayEl) {
    const dateStr = dayEl.dataset.date;
    this.formData.date = dateStr;

    const allDays = this.calendarDays.querySelectorAll('[data-calendar-day]');
    for (const d of allDays) {
      d.classList.remove(`${PREFIX}__calendar-day--selected`);
    }

    dayEl.classList.add(`${PREFIX}__calendar-day--selected`);

    /* Clear any previously selected time if it's now disabled */
    this.updateTimeslotAvailability(dateStr);
  }

  /**
   * Disables time slots that have already passed when
   * the selected date is today. Re-enables all slots for future dates.
   */
  updateTimeslotAvailability(dateStr) {
    if (!this.timeslotGrid) return;

    const todayStr = this.formatDateISO(this.today);
    const isToday = dateStr === todayStr;
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    const slots = this.timeslotGrid.querySelectorAll('[data-timeslot]');

    for (const slot of slots) {
      const slotHour = Number(slot.dataset.timeslotHour);
      const slotMinute = Number(slot.dataset.timeslotMinute);

      const isPast = isToday && (slotHour < currentHour || (slotHour === currentHour && slotMinute <= currentMinute));

      slot.classList.toggle(`${PREFIX}__timeslot--disabled`, isPast);
      slot.setAttribute('aria-disabled', String(isPast));

      if (isPast) {
        slot.setAttribute('tabindex', '-1');
        /* Deselect if the currently selected slot is now past */
        if (slot.classList.contains(`${PREFIX}__timeslot--selected`)) {
          slot.classList.remove(`${PREFIX}__timeslot--selected`);
          slot.setAttribute('aria-pressed', 'false');
          this.formData.time = '';
        }
      } else {
        slot.setAttribute('tabindex', '0');
      }
    }
  }

  formatDateISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  formatDateDisplay(dateStr) {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  /* =========================================
     Time Slots
     ========================================= */

  selectTimeslot(slotEl) {
    const time = slotEl.dataset.timeslot;
    this.formData.time = time;

    const allSlots = this.timeslotGrid.querySelectorAll('[data-timeslot]');
    for (const s of allSlots) {
      s.classList.remove(`${PREFIX}__timeslot--selected`);
      s.setAttribute('aria-pressed', 'false');
    }

    slotEl.classList.add(`${PREFIX}__timeslot--selected`);
    slotEl.setAttribute('aria-pressed', 'true');
  }

  /* =========================================
     Form Submission
     ========================================= */

  async handleSubmit() {
    if (!this.validateStep(3)) return;
    this.collectStepData(3);

    const submitBtn = this.querySelector('[data-action="submit"]');
    if (submitBtn) {
      submitBtn.classList.add(`${PREFIX}__btn--loading`);
      submitBtn.disabled = true;
    }

    const success = await this.submitToGoogleSheet();

    if (this.sendEmail) {
      await this.sendEmailNotification();
    }

    if (submitBtn) {
      submitBtn.classList.remove(`${PREFIX}__btn--loading`);
      submitBtn.disabled = false;
    }

    if (success) {
      this.rememberIdentity();
      this.showConfirmation();
    }
  }

  async submitToGoogleSheet() {
    if (!this.googleScriptUrl) {
      this.showConfirmation();
      return true;
    }

    const payload = {
      timestamp: new Date().toISOString(),
      preference: this.formData.preference,
      city: this.formData.city,
      pincode: this.formData.pincode,
      storeLocation: this.formData.storeLocation,
      purposes: this.formData.purposes.join(', '),
      date: this.formatDateDisplay(this.formData.date),
      time: this.formData.time,
      contactNumber: this.formData.contactNumber,
      firstName: this.formData.firstName,
      lastName: this.formData.lastName,
      email: this.formData.email,
    };

    try {
      await fetch(this.googleScriptUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload),
      });

      return true;
    } catch (error) {
      console.error('BookAppointment: Google Sheet submission failed', error);
      this.showToast('Something went wrong. Please try again.', true);
      return false;
    }
  }

  /* =========================================
     Email Notification (Shopify Contact Form)
     ========================================= */

  async sendEmailNotification() {
    const body = [
      `Preference: ${this.formData.preference}`,
      `City: ${this.formData.city}`,
      `Pincode: ${this.formData.pincode}`,
      `Store Location: ${this.formData.storeLocation}`,
      `Purpose(s): ${this.formData.purposes.join(', ')}`,
      `Date: ${this.formatDateDisplay(this.formData.date)}`,
      `Time: ${this.formData.time}`,
      `Contact Number: ${this.formData.contactNumber}`,
      `Name: ${this.formData.firstName} ${this.formData.lastName}`,
      `Email: ${this.formData.email}`,
    ].join('\n');

    const formData = new FormData();
    formData.append('form_type', 'contact');
    formData.append('utf8', '\u2713');
    formData.append('contact[email]', this.formData.email);
    formData.append('contact[name]', `${this.formData.firstName} ${this.formData.lastName}`);
    formData.append('contact[phone]', this.formData.contactNumber);
    formData.append('contact[body]', `[Book an Appointment]\n\n${body}`);

    try {
      await fetch('/contact#contact_form', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' },
      });
    } catch (error) {
      console.error('BookAppointment: Email notification failed', error);
    }
  }

  /* =========================================
     Confirmation
     ========================================= */

  /**
   * Remembers the visitor so Browse Abandon has a ContactKey later.
   * Optional chaining keeps this a no-op if the helper is not loaded.
   */
  rememberIdentity() {
    const { email, firstName, lastName, contactNumber } = this.formData;

    window.ZdSfmcIdentity?.save({
      email,
      name: `${firstName} ${lastName}`.trim(),
      phone: contactNumber,
    });
  }

  showConfirmation() {
    if (this.formArea) {
      this.formArea.classList.add(`${PREFIX}__form-area--hidden`);
    }

    const stepper = this.querySelector('[data-stepper]');
    if (stepper) {
      stepper.classList.add(`${PREFIX}__stepper--hidden`);
    }

    if (this.confirmation) {
      this.confirmation.classList.add(`${PREFIX}__confirmation--active`);
    }

    this.renderSummary();
    this.scrollToTop();
  }

  renderSummary() {
    if (!this.summaryBody) return;

    const rows = [
      { label: 'Preference', value: this.formData.preference },
      { label: 'City / Country', value: this.formData.city },
      { label: 'Pincode', value: this.formData.pincode },
      { label: 'Store Location', value: this.formData.storeLocation },
      { label: 'Purpose(s)', value: this.formData.purposes.join(', ') },
      { label: 'Date', value: this.formatDateDisplay(this.formData.date) },
      { label: 'Time', value: this.formData.time },
      { label: 'Contact Number', value: this.formData.contactNumber },
      { label: 'Name', value: `${this.formData.firstName} ${this.formData.lastName}` },
      { label: 'Email', value: this.formData.email },
    ];

    const filteredRows = rows.filter((row) => row.value?.trim());

    let html = '';
    for (const row of filteredRows) {
      html += `<div class="${PREFIX}__summary-row">
        <span class="${PREFIX}__summary-label">${row.label}</span>
        <span class="${PREFIX}__summary-value">${this.escapeHTML(row.value)}</span>
      </div>`;
    }

    this.summaryBody.innerHTML = html;
  }

  escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* =========================================
     Toast Notifications
     ========================================= */

  showToast(message, isError = false) {
    if (!this.toast) return;

    this.toast.textContent = message;
    this.toast.classList.toggle(`${PREFIX}__toast--error`, isError);
    this.toast.classList.add(`${PREFIX}__toast--visible`);

    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toast.classList.remove(`${PREFIX}__toast--visible`);
    }, 3000);
  }

  disconnectedCallback() {
    clearTimeout(this.toastTimer);
  }
}

if (!customElements.get('book-appointment')) {
  customElements.define('book-appointment', BookAppointment);
}
